'use client';

import { useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { SegmentedControl } from '@/shared/ui/SegmentedControl';
import {
  proPlanPeriodOptions,
  resolveProPlanOffer,
  type ProPlanOfferSource,
  type ProPlanOfferView,
  type ProPlanPeriod,
} from '@/features/subscription/lib/pro-plan-period';

/**
 * Monthly / Yearly switch for a Pro Plan card (same spec on Apple and
 * Android). Returns the fields the card shows for the selected period plus
 * the switch element, which is null when it must not render (no annual offer,
 * already owned). Selection defaults to Monthly and is not persisted.
 */
export function useProPlanPeriod(source: ProPlanOfferSource | null): {
  offer: ProPlanOfferView | null;
  switcher: ReactNode;
} {
  const tCheckout = useTranslations('subscriptionCheckout');
  const [period, setPeriod] = useState<ProPlanPeriod>('month');

  if (!source) return { offer: null, switcher: null };

  const options = proPlanPeriodOptions(source);
  const offer = resolveProPlanOffer(
    source,
    period,
    tCheckout('pricingSummary.perMonth')
  );
  const switcher = options ? (
    <SegmentedControl
      options={options}
      value={offer.period}
      onChange={(value) => setPeriod(value === 'year' ? 'year' : 'month')}
      fullWidth
    />
  ) : null;

  return { offer, switcher };
}
